import {
  GaleKycIssueAction,
  type HsaGaleOnboardingIssue,
} from "./hsa-gale-onboarding-event";

/**
 * The merchant app route that starts (or resumes) Gale identity verification
 * for a team. Opening it mints a fresh hosted onboarding link server-side (the
 * merchant `startGaleKyc` RPC) and redirects there, so unlike the hosted link
 * itself this path never expires. Every surface that sends a merchant back
 * into KYC (Redo Admin, the SE Slack alert, the merchant email) builds its URL
 * from this one path.
 */
export function galeKycPath(teamId: string): string {
  return `/stores/${teamId}/conversion/kyc`;
}

/** Lines shared by codes Gale maps to the same ask. */
const OWNERSHIP_GUIDANCE =
  "Our payments partner needs details about the people who own or control your business. Open the link below and complete the ownership section, which shows who needs to be listed.";
const SIGNATORY_GUIDANCE =
  "Details for the person who can sign for your business are missing or could not be verified. Open the link below and complete that section.";

/**
 * Redo's "what this means and what to do" line per Gale KYC issue code, shown
 * beneath Gale's message on Redo's own surfaces (the merchant email, the SE
 * Slack alert, later an in-app interstitial). It lives here because Redo
 * cannot change the copy on Adyen's hosted verification page.
 *
 * The map is optional, per-code polish, never a requirement: the watcher and
 * the email work with any code Gale or Adyen returns, and a code missing here
 * gets the generic line for its action instead. So the lines must not assert
 * anything that depends on how Adyen is configured today (ownership
 * thresholds, accepted document types, which roles are mandatory): they say in
 * plain words what the error means and send the merchant to the verification
 * page, which shows Adyen's current requirements. Codes are Adyen verification
 * error codes as Gale maps them (`AdyenOnboardingIssues`) plus Gale's own
 * lifecycle codes.
 */
export const GALE_KYC_ISSUE_GUIDANCE: Readonly<Record<string, string>> = {
  // Ownership details missing or incomplete.
  "2_8219": OWNERSHIP_GUIDANCE,
  "2_8189": OWNERSHIP_GUIDANCE,
  // Bank document rejected, or the account holder does not match.
  "1_75":
    "Your bank document could not be verified. Upload a clear, complete, uncropped copy where the account holder name matches your legal business name.",
  "1_7012":
    "The name on your bank account does not match your legal business name. Upload a clear, complete, uncropped bank document where the account holder name matches it.",
  // Signatory details missing or unverified.
  "2_8218": SIGNATORY_GUIDANCE,
  "2_8067": SIGNATORY_GUIDANCE,
  // Place of business.
  "2_8216":
    "We could not verify where your business operates. Open the link below and check the business address.",
  // A field value Adyen rejected.
  "30_102":
    "One of the details you entered could not be accepted. Open the link below and correct the highlighted field.",
  // Gale lifecycle: the hosted link the merchant had is gone.
  ONBOARDING_LINK_EXPIRED:
    "Your previous verification page expired. The link below opens a fresh one.",
};

/** The fallback for a code with no entry above, by Gale's suggested action. */
const ACTION_GUIDANCE: Readonly<Partial<Record<GaleKycIssueAction, string>>> = {
  [GaleKycIssueAction.RegenerateLink]:
    "Open the link below and review the details flagged on the verification page, then update them.",
  [GaleKycIssueAction.Resubmit]:
    "Review your details on the verification page and submit them again.",
};

/** The last resort: an unmapped code with an action we have no line for. */
const GENERIC_GUIDANCE =
  "Open the link below to see what the verification page still needs from you.";

/** Where an issue's guidance line came from. */
export enum GaleKycIssueGuidanceSource {
  /** A line written for this code. */
  Code = "code",
  /** The generic line for the issue's action; the code has no entry yet. */
  Action = "action",
  /** The universal line; neither the code nor the action has one. */
  Generic = "generic",
}

export interface GaleKycIssueGuidance {
  text: string;
  source: GaleKycIssueGuidanceSource;
}

/**
 * The guidance line for an issue. Never empty: code-specific when the map has
 * the code, else generic for the issue's action, else the universal line, so
 * an issue is never dropped or left bare because its code is new.
 */
export function resolveGaleKycIssueGuidance(
  issue: Pick<HsaGaleOnboardingIssue, "code" | "action">,
): GaleKycIssueGuidance {
  const forCode = ownEntry(GALE_KYC_ISSUE_GUIDANCE, issue.code);
  if (forCode) {
    return { text: forCode, source: GaleKycIssueGuidanceSource.Code };
  }
  const forAction = issue.action
    ? ownEntry(ACTION_GUIDANCE, issue.action)
    : undefined;
  if (forAction) {
    return { text: forAction, source: GaleKycIssueGuidanceSource.Action };
  }
  return { text: GENERIC_GUIDANCE, source: GaleKycIssueGuidanceSource.Generic };
}

/** Whether Redo has a line written for this code, as opposed to a fallback. */
export function hasGaleKycIssueCodeGuidance(code: string): boolean {
  return ownEntry(GALE_KYC_ISSUE_GUIDANCE, code) !== undefined;
}

/** Partner-supplied keys like `__proto__` must never reach the prototype. */
function ownEntry(
  map: Readonly<Partial<Record<string, string>>>,
  key: string,
): string | undefined {
  return Object.hasOwn(map, key) ? map[key] : undefined;
}
